import styled, { css } from "styled-components";
import { COLOURS } from "../../variables/Colors";
import { BREAKPOINTS } from "../../variables/Breakpoints";
import { setFlex, setPosition } from "../../variables/Position";

const setWidth = width => {
  switch (width) {
    case "small":
      return css`
        width: 25%;
        @media (max-width: ${BREAKPOINTS.lg}px) {
          width: 35%;
        }
        @media (max-width: ${BREAKPOINTS.md}px) {
          width: 50%;
        }
        @media (max-width: ${BREAKPOINTS.sm}px) {
          width: 100%;
        }
      `;
    case "medium":
      return css`
        width: 50%;
        @media (max-width: ${BREAKPOINTS.md}px) {
          width: 70%;
        }
        @media (max-width: ${BREAKPOINTS.sm}px) {
          width: 100%;
        }
      `;
    case "large":
      return css`
        width: 80%;
        @media (max-width: ${BREAKPOINTS.sm}px) {
          width: 100%;
        }
      `;
    case "xlarge":
      return css`
        width: 100%;
      `;
    default:
      return css``;
  }
};

const setFont = size => {
  switch (size) {
    case "normal":
      return css`
        font-family: "overpass-regular";
        font-size: 1rem;
        line-height: 1rem;
      `;
    case "large":
      return css`
        font-family: "overpass-bold";
        font-size: 2.35rem;
        line-height: 2.35rem;
        @media (max-width: ${BREAKPOINTS.sm}px) {
          font-size: 1.8rem;
          line-height: 1.8rem;
        }
      `;
    default:
      return css`
        font-family: "overpass-regular";
        font-size: 1rem;
        line-height: 1rem;
      `;
  }
};

export const TextContainer = styled.div`
  ${setPosition({ position: "relative" })};
  ${props =>
    props.$flex &&
    setFlex({
      justify: props.$justify || "center",
      align: props.$align,
      direction: props.$direction || "column",
    })};
  width: ${props => props.$width || "100%"};
  min-height: ${props => props.$height || "auto"};
  margin: ${props => props.$margin || "3rem 0"};
  ${props => props.$zIndex && "z-index: 1"};
  @media (max-width: ${BREAKPOINTS.md}px) {
    width: 100%;
  }
  ${props =>
    !props.$mobileRow &&
    css`
      @media (max-width: ${BREAKPOINTS.md}px) {
        flex-direction: column;
      }
      @media (max-width: ${BREAKPOINTS.xs}px) {
        min-height: auto;
      }
    `}
`;

export const AbsoluteText = styled.div`
  ${setPosition({ position: "absolute", bottom: "0", left: -4 })};
  ${props =>
    props.$flex &&
    setFlex({
      justify: props.$justify || "center",
      align: props.$align,
      direction: props.$direction || "column",
    })};
  min-height: auto;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    ${setPosition({ position: "relative", bottom: "unset", left: "unset" })};
    margin-top: 2rem;
  }
`;

export const Headline = styled.h2`
  font-family: "overpass-bold";
  font-size: 11.25rem;
  line-height: 10.5rem;
  color: ${COLOURS.primary.yellow};
  margin: 0;
  text-transform: uppercase;
  text-align: ${props => props.$textAlign || "center"};
  @media (max-width: ${BREAKPOINTS.lg}px) {
    font-size: 10rem;
    line-height: 9.5rem;
  }
  @media (max-width: ${BREAKPOINTS.md}px) {
    font-size: 8rem;
    line-height: 7.5rem;
  }
  @media (max-width: ${BREAKPOINTS.sm}px) {
    font-size: 7rem;
    line-height: 6.5rem;
  }
  @media (max-width: ${BREAKPOINTS.sm}px) {
    font-size: 4rem;
    line-height: 3.5rem;
  }
`;

export const HeadlineContainer = styled.div`
  ${setFlex({ direction: "column" })};
  ${setPosition({ position: "absolute" })};
  height: fit-content;
  width: 100%;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    ${setPosition({ position: "unset" })};
    margin: 3rem 0;
  }
`;

export const Subheading = styled.h4`
  font-family: "overpass-lotus-headlines";
  font-size: 1.5rem;
  line-height: 1.5rem;
  color: ${COLOURS.primary.yellow};
  margin: 0;
  text-align: ${props => props.$textAlign || "center"};
  font-weight: ${props => props.$weight || "unset"};
  ${props => setWidth(props.$width)};
`;

export const Paragraph = styled.p`
  color: ${COLOURS.primary.yellow};
  margin: 0;
  text-align: ${props => props.$textAlign || "center"};
  ${props => props.$capitalize && "text-transform: uppercase"};
  ${props => props.$indent && "text-indent: 3em"};
  ${props => setWidth(props.$width)};
  ${props => setFont(props.$size)};
  ${props => props.$bold && 'font-family: "overpass-bold"'};
  .highlight {
    font-family: "overpass-lotus-headlines";
    font-size: 1.5rem;
    line-height: 1.5rem;
  }
  a {
    color: ${COLOURS.primary.yellow}!important;
    text-decoration: underline;
  }
`;

export const Link = styled.a`
  font-family: ${props =>
    props.$heading ? "overpass-lotus-headlines" : "overpass-regular"};
  font-size: ${props => (props.$heading ? "1.5rem" : "1rem")};
  line-height: ${props => (props.$heading ? "1.5rem" : "1rem")};
  color: ${COLOURS.primary.yellow}!important;
  text-decoration: underline;
  margin: 0;
`;

export const Banner = styled.p`
  font-family: "overpass-regular";
  font-size: 0.8rem;
  line-height: 0.8rem;
  color: ${COLOURS.primary.yellow};
  margin: 0;
  text-decoration: underline;
  text-underline-offset: 5px;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    font-size: 0.5rem;
  }
  ${props =>
    props.$heading &&
    `
  font-family: "overpass-bold";
  text-decoration: none;
  text-transform: uppercase;
  font-size: 1.2rem;
  letter-spacing: 0.1rem;
    @media (max-width: ${BREAKPOINTS.sm}px) {
      font-size: 1rem;
    }
  `};
`;

export const Block = styled.div`
  background-color: ${COLOURS.primary.yellow};
  height: auto;
  min-width: 35px;
`;

export const ImageBlock = styled.div`
  ${setPosition({ position: "relative" })};
  ${setFlex({})};
  flex: 1;
  ${props => props.$zIndex && "z-index: 1"};
  @media (max-width: ${BREAKPOINTS.md}px) {
    flex: auto;
  }
`;

export const ImageContainer = styled.div`
  min-width: fit-content;
  height: fit-content;
  ${props => props.$full && "width: 100%"};
`;

export const Image = styled.img`
  width: ${props => (props.$full ? "100%" : "auto")};
  max-width: 100%;
`;
